/**
 * Data source vocabulary (Phase 1) — UI labels for rules.type.
 * See docs/monitoring/RULE_BUILDER_TYPE_RESTRUCTURING.md
 */

import {
  formatProcessingCategoryLabel,
  processingCategoryFromBackend,
} from './processingCategory'

export const DATA_SOURCE_OPTIONS = [
  {
    value: 'transaction',
    label: 'Gaming — meter / combined',
    description:
      'Evaluates meter and combined session fields from gaming ingest (CashIn, Turnover, *_alloc).',
    summaryFields: 'CashIn, Turnover, CashIn_alloc, Turnover_alloc (AUD)',
    pipelineNeeds: ['sessions', 'meter', 'enrich', 'allocate'],
  },
  {
    value: 'session',
    label: 'Gaming — session analytics',
    description:
      'Session analytics with period comparison, device/time patterns, and sessions.enriched.* fields.',
    summaryFields: 'sessions.enriched.*, period/device/time patterns',
    pipelineNeeds: ['sessions', 'meter', 'enrich'],
    note: 'Enables period/device pattern rules and richer alert evidence.',
  },
  {
    value: 'onboarding',
    label: 'Member — onboarding / profile',
    description: 'Patron import, signup, and KYC lifecycle events (profile.* fields).',
    summaryFields: 'profile.*, PEP/sanctions at join',
    pipelineNeeds: ['patron import'],
  },
]

const GRAIN_RUNS_ON = {
  'session-based': 'Merged gaming session (gaming_session_enriched)',
  'interval-based': 'Interval attribution rows (gaming_session_interval_attribution)',
  cumulative: 'Member rollups (member_transaction_rollup / daily stats)',
}

const GRAIN_PIPELINE_NOTE = {
  'session-based':
    'Requires session import + Process & Combine for trading day before dry-run or live evaluation.',
  'interval-based':
    'Requires meter import + Process & Combine + interval allocation before dry-run or live evaluation.',
  cumulative: 'Requires member rollup / daily stats pipeline for the configured time window.',
}

/** Map stored rules.type to form select value (backend type, interim). */
export function dataSourceFromBackend(type) {
  const t = String(type || '').toLowerCase()
  if (t === 'session') return 'session'
  if (t === 'onboarding') return 'onboarding'
  if (t === 'payout') return 'payout'
  return 'transaction'
}

export function formatDataSourceLabel(ruleType) {
  if (!ruleType) return '—'
  const value = dataSourceFromBackend(ruleType)
  const opt = DATA_SOURCE_OPTIONS.find((o) => o.value === value)
  if (opt) return opt.label
  if (value === 'payout') return 'Cage payout (future)'
  return String(ruleType)
}

export function dataSourceExplanation(ruleType) {
  const value = dataSourceFromBackend(ruleType)
  const opt = DATA_SOURCE_OPTIONS.find((o) => o.value === value)
  return opt?.description || null
}

export function dataSourceHelperText() {
  return 'Data source chooses which feed supplies field values. For bill stuffing and meter rules, choose Gaming — meter / combined. Cage payout rules require the payout capture hook (not yet live for Monitor Safe rules).'
}

/** Read-only summary for Combined Rule Builder strip and list tooltips. */
export function buildRuleSummary({ dataSource, processingCategory }) {
  const dsValue = dataSourceFromBackend(dataSource)
  const ds =
    DATA_SOURCE_OPTIONS.find((o) => o.value === dsValue) || DATA_SOURCE_OPTIONS[0]
  const grainKey = processingCategoryFromBackend(processingCategory)
  return {
    dataSourceLabel: ds.label,
    grainLabel: formatProcessingCategoryLabel(processingCategory),
    runsOn: GRAIN_RUNS_ON[grainKey] || '—',
    pipelineNeeds: ds.pipelineNeeds || [],
    pipelineNote: GRAIN_PIPELINE_NOTE[grainKey] || null,
    fieldsHint: ds.summaryFields || null,
    dataSourceNote: ds.note || null,
  }
}
